/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentType, CSSProperties, JSX, MouseEvent, ReactNode } from 'react';

import { css, cssMap, cx, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import Tag, { type NewTagColor } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

import { getStyleProps } from '../get-style-props';
import { useSelectGetStyles } from '../internal/use-select-get-styles';
import type { CommonPropsAndClassName, GroupBase, MultiValueGenericProps } from '../types';

import { MultiValueContainer as DefaultMultiValueContainer } from './containers/multi-value-container';
import { MultiValueLabel } from './multi-value-label';
import type { MultiValueRemoveProps } from './multi-value-remove';

interface MultiValueComponents<Option, IsMulti extends boolean, Group extends GroupBase<Option>> {
	Container: ComponentType<MultiValueGenericProps<Option, IsMulti, Group>>;
	Label: ComponentType<MultiValueGenericProps<Option, IsMulti, Group>>;
	Remove: ComponentType<MultiValueRemoveProps<Option, IsMulti, Group>>;
}

export interface MultiValueProps<
	Option = unknown,
	IsMulti extends boolean = boolean,
	Group extends GroupBase<Option> = GroupBase<Option>,
> extends CommonPropsAndClassName<Option, IsMulti, Group> {
	children: ReactNode;
	components: MultiValueComponents<Option, IsMulti, Group>;
	// eslint-disable-next-line @repo/internal/react/boolean-prop-naming-convention
	cropWithEllipsis?: boolean;
	// eslint-disable-next-line @repo/internal/react/consistent-props-definitions
	data: Option;
	innerProps: JSX.IntrinsicElements['div'];
	isFocused: boolean;
	isDisabled: boolean;
	removeProps: JSX.IntrinsicElements['div'];
	index: number;
}

const multiValueTagWrapperStyles = cssMap({
	root: {
		alignItems: 'center',
		display: 'flex',
		flex: '0 1 auto',
		minWidth: token('space.0'),
		marginBlock: token('space.0'),
		marginInline: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- styling Tag component wrapper
		'& > *': {
			alignItems: 'center',
			display: 'flex',
			flex: '1 1 0',
			minWidth: token('space.0'),
		},
	},
});

// Tag-like color styles
const tagLikeColorStyles = cssMap({
	gray: { '--tag-border-token': token('color.border.accent.gray.subtle') },
	blue: { '--tag-border-token': token('color.border.accent.blue.subtle') },
	green: { '--tag-border-token': token('color.border.accent.green.subtle') },
	red: { '--tag-border-token': token('color.border.accent.red.subtle') },
	yellow: { '--tag-border-token': token('color.border.accent.yellow.subtle') },
	purple: { '--tag-border-token': token('color.border.accent.purple.subtle') },
	lime: { '--tag-border-token': token('color.border.accent.lime.subtle') },
	magenta: { '--tag-border-token': token('color.border.accent.magenta.subtle') },
	orange: { '--tag-border-token': token('color.border.accent.orange.subtle') },
	teal: { '--tag-border-token': token('color.border.accent.teal.subtle') },
});

const tagLikeBorderFilterStyles = css({
	borderColor: 'var(--tag-border-token)',
});

// Tag-like styles for custom content values (not plain text) when FF is on.
// Mirrors the TagNew component's visual appearance (padding, margins, sizing, colors).
// The container uses tag-like styling while Label/Remove sub-components are preserved
// so custom overrides (e.g. custom aria-labels, rendering objects as data) continue to work.
const multiValueTagLikeStyles = cssMap({
	root: {
		display: 'inline-flex',
		boxSizing: 'border-box',
		position: 'relative',
		alignItems: 'center',
		alignSelf: 'center',
		minWidth: token('space.0'),
		flexShrink: 1,
		maxWidth: '11.25rem',
		height: '1.25rem',
		overflow: 'hidden',
		borderRadius: token('radius.small', '4px'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		backgroundColor: token('color.background.neutral.subtle'),
		font: token('font.body.small'),
		color: token('color.text'),
		cursor: 'default',
		marginBlock: token('space.0'),
		marginInline: token('space.0'),
		'@media screen and (-ms-high-contrast: active)': {
			border: 'none',
		},
	},
	// Wrapper around Label: flex 1 1 0 = take remaining space, shrink first (Remove has flexShrink: 0).
	// Text truncates, X stays visible. minWidth: 0 required for flex child to shrink below content size.
	labelWrapper: {
		display: 'flex',
		alignItems: 'center',
		flex: '1 1 0',
		minWidth: token('space.0'),
		minHeight: 0,
		overflow: 'hidden',
		font: token('font.body.small'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- force custom Label content to inherit tag typography
		'& *': {
			font: 'inherit',
		},
	},
	disabled: {
		color: token('color.text.disabled'),
		backgroundColor: token('color.background.neutral'),
	},
	focused: {
		color: token('color.text.selected'),
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		boxShadow: `0 0 0 2px ${token('elevation.surface')}, 0 0 0 4px ${token('color.border.focused')}`,
		'@media screen and (-ms-high-contrast: active)': {
			borderWidth: token('border.width'),
			borderColor: 'transparent',
			borderStyle: 'solid',
		},
	},
});

const getMultiValueLabelText = (children: ReactNode, data: unknown): string => {
	if (typeof children === 'string') {
		return children;
	}
	const label = (data as { label?: string })?.label;
	return typeof label === 'string' ? label : '';
};

const MultiValue: <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
	props: MultiValueProps<Option, IsMulti, Group>,
) => JSX.Element = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
	props: MultiValueProps<Option, IsMulti, Group>,
) => {
	const {
		children,
		components,
		data,
		innerProps,
		isDisabled,
		isFocused,
		removeProps,
		selectProps,
		cropWithEllipsis,
	} = props;

	const { Container, Label, Remove } = components;
	const labelText = getMultiValueLabelText(children, data);
	const isPlainLabel = typeof children === 'string';
	const ffTagUplifts = fg('platform-dst-lozenge-tag-badge-visual-uplifts');

	const { css: containerCss, className: containerClassName } = getStyleProps(props, 'multiValue', {
		'multi-value': true,
		'multi-value--is-disabled': isDisabled,
	});

	const { css: labelCss, className: labelClassName } = getStyleProps(props, 'multiValueLabel', {
		'multi-value__label': true,
	});

	const { css: removeCss, className: removeClassName } = getStyleProps(props, 'multiValueRemove', {
		'multi-value__remove': true,
	});

	const hasCustomLabel = Label !== MultiValueLabel;
	const hasCustomContainer = Container !== DefaultMultiValueContainer;
	const selectStyles = selectProps.styles;
	const hasCustomMultiValueStyles = Boolean(
		selectStyles?.multiValue || selectStyles?.multiValueLabel || selectStyles?.multiValueRemove,
	);
	const selectClassNames = selectProps.classNames;
	const hasCustomMultiValueClassNames = Boolean(
		selectClassNames?.multiValue ||
		selectClassNames?.multiValueLabel ||
		selectClassNames?.multiValueRemove,
	);
	// Detect if getStyles was overridden directly on this component instance (e.g. via the
	// components prop wrapper passing a custom getStyles). The context holds the original
	// Select instance's getStyles — a reference inequality means a custom override was passed.
	// When context is absent (e.g. in mocked/test contexts), assume no override.
	const selectGetStyles = useSelectGetStyles();
	const hasOverriddenGetStyles =
		selectGetStyles !== undefined && props.getStyles !== selectGetStyles;
	const hasCustomContainerStyles =
		hasCustomMultiValueStyles || hasCustomMultiValueClassNames || hasOverriddenGetStyles;

	if (
		ffTagUplifts &&
		isPlainLabel &&
		!hasCustomLabel &&
		!hasCustomContainer &&
		!hasCustomContainerStyles
	) {
		const { elemBefore, color: tagColor } = (data ?? {}) as {
			elemBefore?: ReactNode;
			color?: NewTagColor;
		};

		return (
			<div
				css={multiValueTagWrapperStyles.root}
				{...innerProps}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/local-cx-xcss, @compiled/local-cx-xcss
				className={cx(props.className as any, containerClassName, props.xcss, '-multiValue')}
			>
				<Tag
					text={labelText}
					isRemovable={!isDisabled}
					removeButtonLabel={`${labelText}, remove`}
					onBeforeRemoveAction={() => {
						removeProps.onClick?.({} as MouseEvent<HTMLDivElement>);
						return false;
					}}
					color={tagColor ?? 'gray'}
					elemBefore={elemBefore}
					hasMargin={false}
				/>
			</div>
		);
	}

	// FF on + custom content → tag-like path
	if (ffTagUplifts && !hasCustomContainer) {
		const colorKey = (data as { color?: string })?.color;

		return (
			<div
				data-multi-value-tag-like="true"
				css={[
					multiValueTagLikeStyles.root,
					tagLikeColorStyles.gray,
					colorKey === 'blue' && tagLikeColorStyles.blue,
					colorKey === 'green' && tagLikeColorStyles.green,
					colorKey === 'red' && tagLikeColorStyles.red,
					colorKey === 'yellow' && tagLikeColorStyles.yellow,
					colorKey === 'purple' && tagLikeColorStyles.purple,
					colorKey === 'lime' && tagLikeColorStyles.lime,
					colorKey === 'magenta' && tagLikeColorStyles.magenta,
					colorKey === 'orange' && tagLikeColorStyles.orange,
					colorKey === 'teal' && tagLikeColorStyles.teal,
					tagLikeBorderFilterStyles,
					isDisabled && multiValueTagLikeStyles.disabled,
					isFocused && multiValueTagLikeStyles.focused,
				]}
				{...innerProps}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- custom styles.multiValue overrides (e.g. colored borders) must be preserved
				style={containerCss as CSSProperties}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/ui-styling-standard/local-cx-xcss, @compiled/local-cx-xcss
				className={cx(props.className as any, containerClassName, props.xcss, '-multiValue')}
			>
				<div css={multiValueTagLikeStyles.labelWrapper}>
					<Label
						data={data}
						innerProps={{
							style: labelCss as CSSProperties,
							className: labelClassName,
						}}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
						className={labelClassName}
						hasEllipsis={cropWithEllipsis || cropWithEllipsis === undefined}
						selectProps={selectProps}
					>
						{children}
					</Label>
				</div>
				<Remove
					data={data}
					innerProps={{
						style: removeCss as CSSProperties,
						className: removeClassName,
						role: 'button',
						tabIndex: -1,
						'aria-label': `${labelText || 'option'}, remove`,
						...removeProps,
					}}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className={removeClassName}
					isDisabled={isDisabled}
					selectProps={selectProps}
				/>
			</div>
		);
	}

	// FF off → default styles
	return (
		<Container
			data={data}
			innerProps={{
				style: containerCss as CSSProperties,
				className: containerClassName,
				...innerProps,
			}}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={containerClassName}
			isFocused={isFocused}
			isDisabled={isDisabled}
			selectProps={selectProps}
		>
			<Label
				data={data}
				innerProps={{
					style: labelCss as CSSProperties,
					className: labelClassName,
				}}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className={labelClassName}
				hasEllipsis={cropWithEllipsis || cropWithEllipsis === undefined}
				selectProps={selectProps}
			>
				{children}
			</Label>
			<Remove
				data={data}
				innerProps={{
					style: removeCss as CSSProperties,
					className: removeClassName,
					role: 'button',
					tabIndex: -1,
					'aria-label': `${labelText || 'option'}, remove`,
					...removeProps,
				}}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className={removeClassName}
				isDisabled={isDisabled}
				selectProps={selectProps}
			/>
		</Container>
	);
};

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default MultiValue;
